import { DialogActionsBar } from "@tql/ui-components-react/kendo/dialogs";
import type { FormRenderProps } from "@tql/ui-components-react/kendo/form";
import { Form, FormElement } from "@tql/ui-components-react/kendo/form";
import { Checkbox } from "@tql/ui-components-react/kendo/inputs";
import { Button } from "@tql/ui-components-react/tql/buttons";

import {
	FieldType,
	ValidationInputField,
} from "~/shared/components/fields/validation-input-field.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import type { UnifiedNotificationRecipient } from "~/shared/schemas/shipment-notification-schema.ts";

import {
	useCreateNotificationRecipient,
	useUpdateNotificationRecipient,
} from "../../hooks/use-notification-recipients.ts";
import { useNotificationTypes } from "../../hooks/use-notification-types.ts";
import styles from "./notification-user-modal.module.css";

const requiredValidator = (value?: string) => {
	if (!value?.trim()) {
		return "This field is required.";
	}
	return "";
};

const emailValidator = (value?: string) => {
	if (!value?.trim()) {
		return "Email is required.";
	}
	if (value.length > 255) {
		return "Email must be 255 characters or fewer.";
	}
	const atIndex = value.indexOf("@");
	const hasValidAt = atIndex > 0 && atIndex < value.length - 1;
	const hasDotAfterAt = hasValidAt && value.slice(atIndex + 1).includes(".");
	if (!hasValidAt || !hasDotAfterAt) {
		return "Enter a valid email address.";
	}
	return "";
};

const typeKey = (id: number) => `type_${String(id)}` as `type_${number}`;

const getInitialValues = (
	editItem?: UnifiedNotificationRecipient,
	typeIds?: number[],
): Record<string, unknown> => {
	const base: Record<string, unknown> = {
		firstName: editItem?.firstName ?? "",
		lastName: editItem?.lastName ?? "",
		email: editItem?.email ?? "",
	};

	const subscribedSet = new Set(editItem?.subscribedNotificationTypeIds ?? []);
	for (const id of typeIds ?? []) {
		base[typeKey(id)] = subscribedSet.has(id);
	}

	return base;
};

type NotificationUserModalProps = {
	readonly isOpen: boolean;
	readonly onClose: () => void;
	readonly customerId: number;
	readonly isInternal: boolean;
	readonly editItem?: UnifiedNotificationRecipient;
	readonly existingEmails?: readonly string[];
};

export const NotificationUserModal = ({
	isOpen,
	onClose,
	customerId,
	isInternal,
	editItem,
	existingEmails = [],
}: NotificationUserModalProps) => {
	const { data: notificationTypes, isLoading: typesLoading } =
		useNotificationTypes();
	const createMutation = useCreateNotificationRecipient();
	const updateMutation = useUpdateNotificationRecipient();

	if (!isOpen) {
		return null;
	}

	const typeIds = notificationTypes?.map((t) => t.id) ?? [];

	const existingEmailSet = new Set(existingEmails.map((e) => e.toLowerCase()));
	// When editing, the current item's email is not a duplicate
	if (editItem?.email) {
		existingEmailSet.delete(editItem.email.toLowerCase());
	}

	const duplicateEmailValidator = (value?: string) => {
		if (value && existingEmailSet.has(value.toLowerCase())) {
			return "This email address already exists.";
		}
		return "";
	};

	const combinedEmailValidator = (value?: string) => {
		return emailValidator(value) || duplicateEmailValidator(value);
	};

	const handleSubmit = (values: Record<string, unknown>) => {
		const selectedTypeIds = typeIds.filter(
			(id) => values[typeKey(id)] === true,
		);

		const firstName = values["firstName"] as string;
		const lastName = values["lastName"] as string;
		const email = values["email"] as string;

		if (editItem) {
			updateMutation.mutate(
				{
					id: editItem.id,
					body: {
						firstName,
						lastName,
						email,
						notificationTypeIds: selectedTypeIds,
					},
				},
				{ onSuccess: () => onClose() },
			);
		} else if (isInternal) {
			createMutation.mutate(
				{
					customerId,
					firstName,
					lastName,
					email,
					notificationTypeIds: selectedTypeIds,
				},
				{ onSuccess: () => onClose() },
			);
		} else {
			createMutation.mutate(
				{
					firstName,
					lastName,
					email,
					notificationTypeIds: selectedTypeIds,
				},
				{ onSuccess: () => onClose() },
			);
		}
	};

	const isSaving = createMutation.isPending || updateMutation.isPending;

	return (
		<Form
			ignoreModified={true}
			initialValues={getInitialValues(editItem, typeIds)}
			onSubmit={(values) => {
				handleSubmit(values);
			}}
			render={(formRenderProps: FormRenderProps) => {
				const allChecked =
					typeIds.length > 0 &&
					typeIds.every(
						(id) => formRenderProps.valueGetter(typeKey(id)) as boolean,
					);

				const firstName = String(
					formRenderProps.valueGetter("firstName") ?? "",
				);
				const lastName = String(formRenderProps.valueGetter("lastName") ?? "");
				const email = String(formRenderProps.valueGetter("email") ?? "");
				const isFormValid =
					!requiredValidator(firstName) &&
					!requiredValidator(lastName) &&
					!combinedEmailValidator(email);

				return (
					<TraxModal
						className={styles.addNotificationUserForm}
						onClose={onClose}
						title={"LTL Shipment Notification"}
						width={"600px"}
					>
						<FormElement>
							<div className={styles.formRow}>
								<ValidationInputField
									fieldType={FieldType.INPUT}
									id={"firstName"}
									label={"First"}
									name={"firstName"}
									required={true}
									validator={requiredValidator}
								/>
								<ValidationInputField
									fieldType={FieldType.INPUT}
									id={"lastName"}
									label={"Last"}
									name={"lastName"}
									required={true}
									validator={requiredValidator}
								/>
								<ValidationInputField
									fieldType={FieldType.INPUT}
									id={"email"}
									label={"Email"}
									name={"email"}
									required={true}
									validator={combinedEmailValidator}
								/>
							</div>
							<div className={styles.eventSection}>
								<div className={styles.eventSectionTitle}>
									Event Subscription Options:
								</div>
								<div className={styles.checkboxGrid}>
									{typesLoading ? (
										<span>Loading notification types...</span>
									) : (
										<>
											<Checkbox
												id={"selectAll"}
												label={"Select All"}
												onChange={(e) => {
													for (const id of typeIds) {
														formRenderProps.onChange(typeKey(id), {
															value: e.value,
														});
													}
												}}
												value={allChecked}
											/>
											{notificationTypes?.map((t) => (
												<Checkbox
													key={t.id}
													id={typeKey(t.id)}
													label={t.typeName}
													onChange={(e) => {
														formRenderProps.onChange(typeKey(t.id), {
															value: e.value,
														});
													}}
													value={
														formRenderProps.valueGetter(
															typeKey(t.id),
														) as boolean
													}
												/>
											))}
										</>
									)}
								</div>
							</div>
						</FormElement>
						<DialogActionsBar layout={"end"}>
							<Button onClick={onClose} variant={"tertiary"}>
								Cancel
							</Button>
							<Button
								disabled={!isFormValid || isSaving}
								onClick={formRenderProps.onSubmit}
								variant={"primary"}
							>
								{isSaving ? "Saving..." : "Save"}
							</Button>
						</DialogActionsBar>
					</TraxModal>
				);
			}}
		/>
	);
};
